import { isEmpty } from 'lodash';

import { trackEventCampaignPostAdded, trackEventCampaignPostRemoved } from '../../tracking/utils/tracking';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import {
  reportMetricsPostAdded,
  reportMetricsPostRemoved
} from 'modules/campaigns/components/campaign-report/metrics/reducers/campaign-report-metrics';
import {
  campaignDeliverableAttachmentApprovalStatusChanged,
  campaignDeliverablePostAdded,
  campaignDeliverablePostRemoved,
  campaignDeliverablesUpdateUsingOfferDeliverables
} from 'modules/campaigns/reducers/campaignDeliverables';
import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, destroy, get, patch, post as postRequest, put } from 'utils/api';
import { __t, applyTranslations, t } from 'utils/language';
import { logError } from 'utils/log';
import { REALM_OUTFIT_STATUS_PENDING } from 'utils/realm-outfits';

applyTranslations(require('./offer-deliverables-reducer.i18n.json'));

const LOADING = 'offerDeliverables/LOADING';
const RECEIVE = 'offerDeliverables/RECEIVE';
const EDIT_DELIVERABLE_FROM_OFFER = 'offerDeliverables/EDIT_DELIVERABLE_FROM_OFFER';
const RESET = 'offerDeliverables/RESET';
const ATTACHMENT_ADDED = 'offerDeliverables/ATTACHMENT_ADDED';
const ATTACHMENT_REMOVED = 'offerDeliverables/ATTACHMENT_REMOVED';
const ATTACHMENT_APPROVAL_STATUS_CHANGED = 'offerDeliverables/ATTACHMENT_APPROVAL_STATUS_CHANGED';
const POST_ADDED = 'offerDeliverables/POST_ADDED';
const POST_REMOVED = 'offerDeliverables/POST_REMOVED';
const WMC_LINK_ADDED = 'offerDeliverables/WMC_LINK_ADDED';
const CONTENT_APPROVAL_UPDATED = 'offerDeliverables/CONTENT_APPROVAL_UPDATED';
const CONTENT_APPROVAL_NOTE_DELETED = 'offerDeliverables/CONTENT_APPROVAL_NOTE_DELETED';
const CONTENT_APPROVAL_NOTE_UPDATED = 'offerDeliverables/CONTENT_APPROVAL_NOTE_UPDATED';
const INSIGHTS_UPDATED = 'offerDeliverables/INSIGHTS_UPDATED';
const OUTFITS_UPDATED = 'offerDeliverables/OUTFITS_UPDATED';
const OUTFITS_DELETED = 'offerDeliverables/OUTFITS_DELETED';
const ENTERTAINMENT_VIDEO_UPDATED = 'offerDeliverables/ENTERTAINMENT_VIDEO_UPDATED';

const { DELIVERABLE_MEDIA, OFFERS } = API.END_POINTS;

import type {
  Action,
  Deliverable,
  DeliverableAttachment,
  DeliverableContentApproval,
  DeliverableGenericTimeline,
  DeliverablePersistedWMCLink,
  DeliverablePost,
  DeliverablesByOfferId,
  RealmOutfit,
  ThunkAction
} from 'types';
import { FullEntertainmentVideo } from 'modules/entertainment-videos/types';

export type DeliverableEditNoteResponse = {
  id: number,
  date: string,
  text: string
}


const initialState: DeliverablesByOfferId = {
  deliverablesByOfferId: {},
  isLoading: false
};

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

export function resetOfferDeliverables(): Action {
  return {
    type: RESET
  };
}

function updateContentApprovalDeletedNoteState(
  offerId: number,
  deliverableId: number,
  noteId: number
): Action {
  return {
    type: CONTENT_APPROVAL_NOTE_DELETED,
    payload: {
      offerId,
      deliverableId,
      noteId
    }
  };
}

function updateContentApprovalNoteState(
  offerId: number,
  deliverableId: number,
  contentApprovalNote: DeliverableEditNoteResponse
): Action {
  return {
    type: CONTENT_APPROVAL_NOTE_UPDATED,
    payload: {
      offerId,
      deliverableId,
      contentApprovalNote
    }
  };
}

export function updateOutfit(
  offerId: number,
  deliverableId: number,
  outfitId: number,
  outfit: RealmOutfit
): Action {
  return {
    type: OUTFITS_UPDATED,
    payload: {
      offerId,
      deliverableId,
      outfitId,
      outfit
    }
  };
}

export function updateEntertainmentVideo(
  offerId: number,
  deliverableId: number,
  entertainmentVideoId: number,
  entertainmentVideo: FullEntertainmentVideo
) {
  return {
    type: ENTERTAINMENT_VIDEO_UPDATED,
    payload: {
      offerId,
      deliverableId,
      entertainmentVideoId,
      entertainmentVideo
    }
  };
}

export function updateContentApprovalState(
  offerId: number,
  deliverableId: number,
  contentApproval: DeliverableContentApproval,
  contentApprovalTimeline: DeliverableGenericTimeline
): Action {
  return {
    type: CONTENT_APPROVAL_UPDATED,
    payload: {
      offerId,
      deliverableId,
      contentApproval,
      contentApprovalTimeline
    }
  };
}

export function updateDeliverableOutfitsOnDelete(
  offerId: number,
  deliverableId: number
): Action {
  return {
    type: OUTFITS_DELETED,
    payload: {
      offerId,
      deliverableId
    }
  };
}

export function updateInsights(
  offerId: number,
  updatedDeliverable: Deliverable,
  post?: DeliverablePost): Action {
  return {
    type: INSIGHTS_UPDATED,
    payload: {
      offerId,
      updatedDeliverable,
      post
    }
  };
}

function receiveOfferDeliverables(offerId: number, data: Array<Deliverable>): Action {
  return {
    type: RECEIVE,
    payload: {
      offerId,
      data
    }
  };
}

export function deliverableAttachmentAdded(offerId: number, deliverableId: number, attachment: DeliverableAttachment): Action {
  return {
    type: ATTACHMENT_ADDED,
    payload: {
      offerId,
      deliverableId,
      attachment
    }
  };
}

export function deliverableAttachmentRemoved(offerId: number, deliverableId: number, attachmentId: number | string): Action {
  return {
    type: ATTACHMENT_REMOVED,
    payload: {
      offerId,
      deliverableId,
      attachmentId
    }
  };
}

function attachmentApprovalStatusChanged(offerId: number, deliverableId: number, attachment: DeliverableAttachment): Action {
  return {
    type: ATTACHMENT_APPROVAL_STATUS_CHANGED,
    payload: {
      offerId,
      deliverableId,
      attachment
    }
  };
}

function deliverablePostAdded(offerId: number, deliverableId: number, post: DeliverablePost): Action {
  return {
    type: POST_ADDED,
    payload: {
      offerId,
      deliverableId,
      post
    }
  };
}

function deliverablePostRemoved(offerId: number, deliverableId: number): Action {
  return {
    type: POST_REMOVED,
    payload: {
      offerId,
      deliverableId
    }
  };
}

export function createDeliverablePost(offerId: number, deliverableId: number, postUrl: string): ThunkAction {
  const url = DELIVERABLE_MEDIA.updateOfferPost(offerId, deliverableId);

  return async (dispatch) => {
    dispatch(showLoading(url));

    let createdPost: DeliverablePost | null = null;

    try {
      const response = (await postRequest(url, {
        data: {
          url: postUrl,
          assetType: 'other'
        }
      })) as { data: DeliverablePost };
      createdPost = response.data;
      dispatch(deliverablePostAdded(offerId, deliverableId, createdPost));
      trackEventCampaignPostAdded(offerId, deliverableId);
      dispatch(hideLoading(url));

      dispatch(reportMetricsPostAdded(deliverableId, createdPost));

      dispatch(campaignDeliverablePostAdded(deliverableId, createdPost)); // Brands Content page
    } catch (error) {
      dispatch(hideLoading(url));
      throw error;
    }

    return createdPost;
  };
}

export function removeDeliverablePost(offerId: number, deliverableId: number): ThunkAction {
  const url = DELIVERABLE_MEDIA.updateOfferPost(offerId, deliverableId);

  return async (dispatch) => {
    dispatch(showLoading(url));

    try {
      await destroy(url);
      dispatch(deliverablePostRemoved(offerId, deliverableId));
      trackEventCampaignPostRemoved(offerId, deliverableId);
      dispatch(hideLoading(url));

      // Posts can be removed on the admin and report metrics pages so we have to update both stores
      dispatch(reportMetricsPostRemoved(deliverableId));

      dispatch(campaignDeliverablePostRemoved(deliverableId)); // Brands Content page
    } catch (error) {
      dispatch(hideLoading(url));
      dispatch(danger(t('unknown_error')));
      throw error;
    }
  };
}

export function deliverableAttachmentApprovalStatusModified(offerId: number, deliverableId: number, attachment: DeliverableAttachment): ThunkAction {
  return (dispatch) => {
    dispatch(attachmentApprovalStatusChanged(offerId, deliverableId, attachment));
    dispatch(campaignDeliverableAttachmentApprovalStatusChanged(deliverableId, attachment)); // Brand Content page
  };
}

export function changeAttachmentsApprovalStatus(
  offerId: number,
  deliverableId: number,
  attachments: Array<DeliverableAttachment>
): ThunkAction {
  const url = DELIVERABLE_MEDIA.changeAttachmentsApprovalStatus(offerId, deliverableId);

  return async (dispatch) => {
    dispatch(showLoading(url));

    try {
      await put(url, { data: attachments });
      dispatch(hideLoading(url));
    } catch (error) {
      dispatch(hideLoading(url));
      dispatch(danger(t('unknown_error')));
      throw error;
    }
  };
}

export function changeDeliverableApprovalNote(offerId: number, deliverableId: number, noteName: string, note: string): ThunkAction {
  const url = noteName === 'creatorNote'
    ? OFFERS.putDeliverableCreatorNote(offerId, deliverableId)
    : OFFERS.putDeliverableBrandFeedback(offerId, deliverableId);

  return async (dispatch) => {
    dispatch(showLoading(url));

    try {
      const data = {
        note
      };
      await put(url, { data });
      dispatch(hideLoading(url));
    } catch (error) {
      dispatch(hideLoading(url));
      dispatch(danger(t('unknown_error')));
      throw error;
    }
  };
}

export function fetchOfferDeliverables(offerId: number, includeMedia: boolean = false): ThunkAction {
  let url = OFFERS.getOfferDeliverables(offerId);

  if (includeMedia) {
    url += '?include=post,attachments,tracking_links';
  }

  return async (dispatch) => {
    dispatch(setLoadingStatus(true));

    let deliverables: Array<Deliverable> = [];

    try {
      const deliverablesResponse = (await get(url)) as { data: Array<Deliverable> };
      deliverables = deliverablesResponse.data;

      dispatch(receiveOfferDeliverables(offerId, deliverables));
    } catch (error) {
      dispatch(setLoadingStatus(false));
      throw error;
    }

    return deliverables;
  };
}

export const updateContentApproval = (
  offerId: number,
  deliverableId: number,
  contentApproval: DeliverableContentApproval,
  contentApprovalTimeline: DeliverableGenericTimeline
): ThunkAction => (
  async (dispatch, getState) => {
    const campaignDeliverables = getState().campaignDeliverables.deliverables;

    // on "Content" page, updated content is read from `campaignDeliverables`, not `offerDeliverables`
    // so to ensure "Content" page has proper data, update its state as well
    const updatedCampaignDeliverables = campaignDeliverables.map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          contentApproval,
          contentApprovalTimeline
        };
      }

      return deliverable;
    });

    dispatch(updateContentApprovalState(offerId, deliverableId, contentApproval, contentApprovalTimeline));
    dispatch(campaignDeliverablesUpdateUsingOfferDeliverables(updatedCampaignDeliverables));
  }
);

const deleteContentApprovalNote = (
  offerId: number,
  deliverableId: number,
  noteId: number
): ThunkAction => (
  async (dispatch, getState) => {
    const campaignDeliverables = getState().campaignDeliverables.deliverables;

    // on "Content" page, updated content is read from `campaignDeliverables`, not `offerDeliverables`
    // so to ensure "Content" page has proper data, update its state as well
    const updatedCampaignDeliverables = campaignDeliverables.map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          contentApproval: {
            ...deliverable.contentApproval,
            approvalNotes: deliverable.contentApproval.approvalNotes.map(note => {
              if (note.id === noteId) {
                return {
                  ...note,
                  isDeleted: true
                };
              }

              return note;
            })
          }
        };
      }

      return deliverable;
    });


    dispatch(updateContentApprovalDeletedNoteState(offerId, deliverableId, noteId));
    dispatch(campaignDeliverablesUpdateUsingOfferDeliverables(updatedCampaignDeliverables));
  }
);

export const updateContentApprovalNote = (
  offerId: number,
  deliverableId: number,
  contentApprovalNote: DeliverableEditNoteResponse
): ThunkAction => (
  async (dispatch, getState) => {
    const campaignDeliverables = getState().campaignDeliverables.deliverables;

    // on "Content" page, updated content is read from `campaignDeliverables`, not `offerDeliverables`
    // so to ensure "Content" page has proper data, update its state as well
    const updatedCampaignDeliverables = campaignDeliverables.map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          contentApproval: {
            ...deliverable.contentApproval,
            approvalNotes: deliverable.contentApproval.approvalNotes.map(note => {
              if (note.id === contentApprovalNote.id) {
                return {
                  ...note,
                  ...contentApprovalNote
                };
              }

              return note;
            })
          }
        };
      }

      return deliverable;
    });

    dispatch(updateContentApprovalNoteState(offerId, deliverableId, contentApprovalNote));
    dispatch(campaignDeliverablesUpdateUsingOfferDeliverables(updatedCampaignDeliverables));
  }
);

export function editApprovalNote(offerId: number, deliverableId: number, noteId: number, noteValue: string) {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.editApprovalNote(noteId);
    dispatch(showLoading(url));

    try {
      const requestMethod = noteValue ? patch : destroy;
      const response = await requestMethod(url, {
        data: { text: noteValue }
      }, { suppressGlobalNotification: true });
      const updatedNote = response?.data || {};

      if (noteValue) {
        dispatch(updateContentApprovalNote(offerId, deliverableId, updatedNote));
      } else {
        dispatch(deleteContentApprovalNote(offerId, deliverableId, noteId));
      }
    } catch (error) {
      dispatch(danger(__t('deliverable_content_approval_reducer.note_update_failed')));
      logError(error);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function deleteDeliverableOutfits(deliverableId: number, offerId: number) {
  return async (dispatch) => {
    const url = API.END_POINTS.REALM_OUTFITS.deleteOutfits(deliverableId);
    dispatch(showLoading(url));

    try {
      await destroy(url);

      dispatch(updateDeliverableOutfitsOnDelete(offerId, deliverableId));
    } catch (error) {
      dispatch(danger(__t('deliverable_content_approval_reducer.delete_outfit_failed')));
      logError(error);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

function addDeliverableWMCLink(
  offerId: number,
  deliverableId: number,
  wmcLinkData: DeliverablePersistedWMCLink
): Action {
  return {
    type: WMC_LINK_ADDED,
    payload: {
      offerId,
      deliverableId,
      wmcLinkData
    }
  };
}

export function fetchOfferDeliverableWMCLinks(offerId: number, deliverableId: number): ThunkAction {
  return async (dispatch) => {
    dispatch(setLoadingStatus(true));
    try {
      const url = API.END_POINTS.OFFERS.offerDeliverableWMCLinks(offerId, deliverableId);
      const { data } = (await get(url)) as { data: DeliverablePersistedWMCLink };

      if (!isEmpty(data)) {
        dispatch(addDeliverableWMCLink(offerId, deliverableId, data));
      }

      return data;
    } catch (error) {
      logError(error);
      throw error;
    }
  };
}

export function moveContent(
  offerId: number,
  deliverableId: number,
  targetDeliverableId: number,
  assetIds: Array<number|string>
): ThunkAction {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.moveContent(offerId, deliverableId);
    try {
      dispatch(showLoading(url));
      await put(url, {
        data: {
          targetDeliverableId,
          ids: assetIds
        }
      }, { suppressGlobalNotification: true });

      dispatch(hideLoading(url));
      dispatch(success(__t('offer_deliverables_reducer.update_success')));
    } catch (error) {
      dispatch(hideLoading(url));
      if (error.response?.status === 404) {
        dispatch(danger(__t('offer_deliverables_reducer.not_found')));
      } else {
        dispatch(danger(__t('offer_deliverables_reducer.update_failed')));
      }
      logError(error);
      throw error;
    }
  };
}

export default createReducer<DeliverablesByOfferId, any>('offerDeliverables', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [RECEIVE]: (state, { offerId, data }) => ({
    ...state,
    deliverablesByOfferId: {
      ...state.deliverablesByOfferId,
      [offerId]: data
    }
  }),
  [EDIT_DELIVERABLE_FROM_OFFER]: (state, { offerId, newDeliverable, oldDeliverable }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const newDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === oldDeliverable.id) {
        return newDeliverable;
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: newDeliverables
      }
    });
  },
  [POST_ADDED]: (state, { offerId, deliverableId, post }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          post
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [POST_REMOVED]: (state, { offerId, deliverableId }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          post: undefined
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [ATTACHMENT_ADDED]: (state, { offerId, deliverableId, attachment }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          postAttachments: [
            ...(deliverable.postAttachments || []),
            attachment
          ]
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [ATTACHMENT_REMOVED]: (state, { offerId, deliverableId, attachmentId }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          postAttachments: (deliverable.postAttachments || []).filter(a => a.id !== attachmentId)
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [ATTACHMENT_APPROVAL_STATUS_CHANGED]: (state, { offerId, deliverableId, attachment }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          postAttachments: (deliverable.postAttachments || []).map(a => (a.id === attachment.id ? attachment : a))
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [WMC_LINK_ADDED]: (state, { offerId, deliverableId, wmcLinkData }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          mostRecentWMCLink: wmcLinkData
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [CONTENT_APPROVAL_UPDATED]: (state, { offerId, deliverableId, contentApproval, contentApprovalTimeline }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          contentApproval,
          contentApprovalTimeline
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [CONTENT_APPROVAL_NOTE_DELETED]: (state, { offerId, deliverableId, noteId }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          contentApproval: {
            ...deliverable.contentApproval,
            approvalNotes: deliverable.contentApproval.approvalNotes.map(note => {
              if (note.id === noteId) {
                return {
                  ...note,
                  isDeleted: true
                };
              }

              return note;
            })
          }
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [CONTENT_APPROVAL_NOTE_UPDATED]: (state, { offerId, deliverableId, contentApprovalNote }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          contentApproval: {
            ...deliverable.contentApproval,
            approvalNotes: deliverable.contentApproval.approvalNotes.map(note => {
              if (note.id === contentApprovalNote.id) {
                return {
                  ...note,
                  ...contentApprovalNote
                };
              }

              return note;
            })
          }
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [INSIGHTS_UPDATED]: (state, { offerId, updatedDeliverable, post }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === updatedDeliverable.id) {
        return {
          ...deliverable,
          postAttachments: updatedDeliverable.postAttachments || [],
          insightsProvisionTimeline: updatedDeliverable.insightsProvisionTimeline,
          post
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [OUTFITS_UPDATED]: (state, { offerId, deliverableId, outfitId, outfit: updatedOutfit }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          outfits: (deliverable.outfits || []).map(outfit => {
            if (outfit.id === outfitId) {
              return updatedOutfit;
            }

            return outfit;
          })
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [OUTFITS_DELETED]: (state, { offerId, deliverableId }) => {
    const deliverables = state.deliverablesByOfferId[offerId];
    const updatedDeliverables = (deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          outfits: (deliverable.outfits || []).filter(outfit => outfit.status !== REALM_OUTFIT_STATUS_PENDING)
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    });
  },
  [ENTERTAINMENT_VIDEO_UPDATED]: (
    state,
    { offerId, deliverableId, entertainmentVideoId, entertainmentVideo: updatedEntertainmentVideo }
  ) => {
    const deliverables = state.deliverablesByOfferId[offerId] ?? [];

    const updatedDeliverables = deliverables.map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          entertainmentVideos: (deliverable.entertainmentVideos ?? []).map(entertainmentVideo => {
            if (entertainmentVideo.entertainmentVideo.id === entertainmentVideoId) {
              return updatedEntertainmentVideo;
            }

            return entertainmentVideo;
          })
        };
      }

      return deliverable;
    });

    return {
      ...state,
      deliverablesByOfferId: {
        ...state.deliverablesByOfferId,
        [offerId]: updatedDeliverables
      }
    };
  }
});
